import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { DeleteJobTypeModal } from "@/components/Organisms/DeleteJobTypeModal/DeleteJobTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { EMPTY_ORG_ID } from "@/constants/org";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useDuplicateJobType,
	useUpdateJobType,
} from "@/services/job/job.mutations";
import { jobQueryOptions } from "@/services/job/job.options";
import {
	canShowActivityTab,
	getJobTypeDisplayName,
} from "@/utils/job-type-detail/job-type-detail.utils";
import {
	JobTypeDrawerContext,
	type JobTypeDrawerContextValue,
} from "@/utils/job-type-detail/job-type-drawer-context";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
});

type JobTypeTab = "details" | "schema" | "lifecycle" | "rules" | "activity";

function getCurrentTab(pathname: string): JobTypeTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "schema" ||
		segment === "lifecycle" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId",
)({
	component: AdminJobTypeIdLayout,
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/jobTypes/${params.jobTypeId}`,
		);
		if (exactRoot) {
			throw redirect({
				to: "/admin/configuration/jobTypes/$jobTypeId/details",
				params: { jobTypeId: params.jobTypeId },
				search: { mode: "view", presentation: "full" },
			});
		}
	},
});

function AdminJobTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFormRoute = useRouterState({
		select: (s) =>
			s.matches.some(
				(m) =>
					typeof m.routeId === "string" &&
					m.routeId.endsWith("/$jobTypeId/form"),
			),
	});
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId",
	});

	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/jobTypes/$jobTypeId",
	});

	const isFullRoute = /\/configuration\/jobTypes\/[^/]+\/full/.test(pathname);

	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(jobQueryOptions.jobTypeDetail(params.jobTypeId));
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteJobType",
			entityType: "JobType",
			entityId: params.jobTypeId,
		}),
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditJobType",
			entityType: "JobType",
			entityId: params.jobTypeId,
		}),
	});
	const updateMutation = useUpdateJobType();
	const duplicateMutation = useDuplicateJobType();
	const { duplicate: duplicateJobType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (jobTypeId) => ({
				jobTypeId,
				organizationId: EMPTY_ORG_ID,
			}),
			onDuplicated: (jobTypeId) => {
				void navigate({
					to: "/admin/configuration/jobTypes/$jobTypeId/details",
					params: { jobTypeId },
					search: { mode: "view", presentation: "full" },
				});
			},
			errorMessage: t("admin.jobTypes.modal.createError"),
		});

	const jobType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		jobType?.id,
		jobType?.locationRequired,
	);

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");

	useEffect(() => {
		if (!jobType) return;
		const name = getJobTypeDisplayName(jobType);
		const description = jobType.description ?? "";
		const icon = jobType.icon ?? "";
		if (search.mode !== "edit" || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (search.mode !== "edit" || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (search.mode !== "edit" || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
	}, [jobType]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/admin/configuration/jobTypes",
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	if (isFormRoute) {
		return (
			<div className="flex min-h-0 flex-1 flex-col bg-background-primary">
				<Outlet />
			</div>
		);
	}

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || jobType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && jobType !== null;

	const loadedJobType = jobType ?? undefined;
	const displayJobType =
		loadedJobType && isDetailReady
			? {
					...loadedJobType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon || loadedJobType.icon,
				}
			: null;

	const canEdit = !!loadedJobType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedJobType &&
		isDetailReady &&
		canShowActivityTab("platform", loadedJobType);
	const currentTab = getCurrentTab(pathname);
	const isEditing = search.mode === "edit";

	const isValid = draftName.trim().length > 0;

	const tabs: { id: JobTypeTab; label: string }[] = isDetailReady
		? [
				{ id: "details", label: t("admin.jobTypes.drawer.tabs.details") },
				{ id: "schema", label: t("admin.jobTypes.drawer.tabs.form") },
				{
					id: "lifecycle",
					label: t("admin.jobTypes.drawer.tabs.lifecycle"),
				},
				{ id: "rules", label: t("admin.jobTypes.drawer.tabs.rules") },
				...(showActivity
					? [
							{
								id: "activity" as const,
								label: t("admin.jobTypes.drawer.tabs.activity"),
							},
						]
					: []),
			]
		: [];

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!loadedJobType && isDetailReady && !!canDeleteQuery.data && !isEditing;
	const canDuplicate = isDetailReady && !isEditing;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedJobType || !isValid) return;
		try {
			await updateMutation.mutateAsync({
				jobTypeId: loadedJobType.id,
				data: {
					name: draftName.trim(),
					description: draftDescription || null,
					icon: draftIcon || null,
					typeId: loadedJobType.id,
					statusLifecycleId: loadedJobType.lifecycleId ?? null,
					locationRequired,
				},
				organizationId: EMPTY_ORG_ID,
			});
			setEditedName(draftName.trim());
			setEditedDescription(draftDescription);
			setEditedIcon(draftIcon);
			showSuccessToast({
				message: t("admin.jobTypes.drawer.details.toast.saveSuccess"),
			});
			void navigate({
				to: `/admin/configuration/jobTypes/${params.jobTypeId}/details`,
				search: { mode: "view" },
			});
		} catch {
			// Global mutation error handler in root-provider.tsx already shows the error toast
		}
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedJobType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		await updateMutation.mutateAsync({
			jobTypeId: loadedJobType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				typeId: loadedJobType.id,
				statusLifecycleId: loadedJobType.lifecycleId ?? null,
				locationRequired,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedJobType) return;
		await updateMutation.mutateAsync({
			jobTypeId: loadedJobType.id,
			data: {
				name: (editedName || loadedJobType.name || "").trim(),
				description: editedDescription || loadedJobType.description || null,
				icon: editedIcon || loadedJobType.icon || null,
				typeId: loadedJobType.id,
				statusLifecycleId: loadedJobType.lifecycleId ?? null,
				locationRequired: next,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setLocationRequired(next);
	};

	const contextValue: JobTypeDrawerContextValue | null =
		loadedJobType && isDetailReady && displayJobType
			? {
					scope: "platform",
					jobType: displayJobType,
					mode: search.mode,
					setMode: (mode) => {
						if (isFullRoute) {
							void navigate({
								to: `/admin/configuration/jobTypes/${params.jobTypeId}/full`,
								search: { tab: "details", mode },
							});
						} else {
							void navigate({
								to: `/admin/configuration/jobTypes/${params.jobTypeId}/details`,
								search: { mode },
							});
						}
					},
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds: null,
					setDraftSubOrgIds: () => {},
					isSaving: updateMutation.isPending,
					isValid,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription ||
						draftIcon !== editedIcon,
					saveDraft: saveDraftFn,
					cancelDraft: () => {
						setDraftName(editedName);
						setDraftDescription(editedDescription);
						setDraftIcon(editedIcon);
						void navigate({
							to: `/admin/configuration/jobTypes/${params.jobTypeId}/details`,
							search: { mode: "view" },
						});
					},
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayJobType !== null
			? getJobTypeDisplayName(displayJobType)
			: t("admin.jobTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedJobType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedJobType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedJobType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedJobType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	if (isFullRoute) {
		return contextValue ? (
			<JobTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</JobTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	// useRouterState updates before the component unmounts, so during navigation
	// away from any $jobTypeId route the pathname briefly shows the new URL.
	// Guard against mounting the Drawer during that window.
	const isOnJobTypeSubroute = /\/configuration\/jobTypes\/[^/]+\//.test(
		pathname,
	);

	if (!isOnJobTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="job"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: `/admin/configuration/jobTypes/${params.jobTypeId}/full`,
						search: { tab: "details", mode: "view" },
					});
				}}
				duplicateLabel={
					canDuplicate ? t("admin.jobTypes.table.actions.duplicate") : undefined
				}
				onDuplicate={
					canDuplicate ? () => duplicateJobType(params.jobTypeId) : undefined
				}
				deleteLabel={
					canDelete
						? t("admin.jobTypes.drawer.footer.deleteJobType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.jobTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.jobTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/admin/configuration/jobTypes/${params.jobTypeId}/${tabId}`,
						search: { mode: search.mode },
					})
				}
				data-testid="job-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<JobTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</JobTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteJobTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				jobTypeId={params.jobTypeId}
				jobTypeName={loadedJobType ? getJobTypeDisplayName(loadedJobType) : ""}
				organizationId={EMPTY_ORG_ID}
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.jobTypes.duplicate.inProgress")}
				testId="jobtype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
